import React, { useEffect, useRef, useState, useCallback } from "react";
import landsdelar from "../data/landsdelar.json";
import { ReactComponent as MapSVG } from "../data/map.svg";

export default function InteractiveMap({ currentDate: initialCurrentDate, onRegionSelect, selectedRegionId }) {
  console.log('InteractiveMap render:', { selectedRegionId, onRegionSelect: !!onRegionSelect });
  
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({ opacity: 0, transform: "translate(-50%, -50%)" });
  const [isTooltipLocked, setIsTooltipLocked] = useState(false);

  const validateCurrentDate = (date) => {
    const now = new Date();
    const stockholmTime = new Date(now.toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' }));
    return date > stockholmTime ? stockholmTime : date;
  };

  useEffect(() => {
    const validatedDate = validateCurrentDate(new Date(initialCurrentDate || Date.now()));
    setCurrentDate(validatedDate);
  }, [initialCurrentDate]);

  // Uppdatera vald region när selectedRegionId ändras från parent
  useEffect(() => {
    if (selectedRegionId) {
      const region = landsdelar.find(r => r.id === selectedRegionId);
      if (region) {
        setSelectedRegion(region);
        setIsTooltipLocked(true);
        setHoveredRegion(region);
        
        // Placera tooltipen i mitten av skärmen
        const x = Math.max(10, (window.innerWidth - 320) / 2);
        const y = Math.max(10, (window.innerHeight - 200) / 2);
        setTooltipStyle({
          left: x,
          top: y,
          opacity: 1,
          transform: "translate(0, 0)"
        });
      }
    } else {
      setSelectedRegion(null);
      setIsTooltipLocked(false);
      setHoveredRegion(null);
      setTooltipStyle(prev => ({ ...prev, opacity: 0 }));
    }
  }, [selectedRegionId]);

  const getRegionColor = useCallback((region) => {
    const currentTime = new Date(currentDate);
    const start = new Date(region.startdatum);
    const end = region.slutdatum ? new Date(region.slutdatum) : null;

    return currentTime >= start && (!end || currentTime <= end)
      ? "#2563eb"
      : "#d1d5db";
  }, [currentDate]);

  // Funktion för att hitta alla paths som matchar ett prefix
  const findPathsByPrefix = useCallback((prefix) => {
    if (!svgRef.current) return [];
    
    const allPaths = svgRef.current.querySelectorAll("path");
    const matchingPaths = [];
    
    allPaths.forEach(path => {
      const pathId = path.id;
      if (pathId && pathId.startsWith(prefix)) {
        matchingPaths.push(path);
      }
    });
    
    return matchingPaths;
  }, []);

  // Funktion för att låsa upp tooltip
  const unlockTooltip = useCallback(() => {
    setSelectedRegion(null);
    setIsTooltipLocked(false);
    setHoveredRegion(null);
    setTooltipStyle(prev => ({ ...prev, opacity: 0 }));
    if (onRegionSelect) {
      onRegionSelect(null);
    }
  }, [onRegionSelect]);

  // Funktion för att hantera klick på en region
  const handleRegionClick = useCallback((region, event) => {
    event.stopPropagation();
    
    if (selectedRegion && selectedRegion.id === region.id) {
      // Om samma region klickas igen, avmarkera den
      unlockTooltip();
    } else {
      // Markera den nya regionen
      setSelectedRegion(region);
      setIsTooltipLocked(true);
      setHoveredRegion(region);
      
      // Placera tooltipen vid muspekarens position
      const x = Math.min(event.clientX, window.innerWidth - 320 - 10);
      const y = Math.min(event.clientY, window.innerHeight - 200 - 10);
      setTooltipStyle({
        left: x + 10,
        top: y + 10,
        opacity: 1,
        transform: "translate(0, 0)"
      });

      // Notifiera parent om vald region
      if (onRegionSelect) {
        onRegionSelect(region.id);
      }
    }
  }, [selectedRegion, unlockTooltip, onRegionSelect]);

  // Funktion för att hantera klick på tomt område
  const handleMapClick = useCallback((event) => {
    // Klicka var som helst på kartan för att låsa upp tooltipen
    unlockTooltip();
  }, [unlockTooltip]);

  // Funktion för att hantera klick utanför kartan
  const handleOutsideClick = useCallback((event) => {
    // Klicka var som helst utanför kartan för att låsa upp tooltipen
    unlockTooltip();
  }, [unlockTooltip]);

  // Funktion för att hantera hover (endast när tooltip inte är låst)
  const handleRegionHover = useCallback((region, event) => {
    if (!isTooltipLocked) {
      setHoveredRegion(region);
      setTooltipStyle(prev => ({ ...prev, opacity: 1 }));
    }
  }, [isTooltipLocked]);

  const handleRegionHoverMove = useCallback((e) => {
    if (!isTooltipLocked) {
      requestAnimationFrame(() => {
        if (tooltipRef.current) {
          const tooltipWidth = tooltipRef.current.offsetWidth;
          const tooltipHeight = tooltipRef.current.offsetHeight;
          const x = Math.min(e.clientX, window.innerWidth - tooltipWidth - 10);
          const y = Math.min(e.clientY, window.innerHeight - tooltipHeight - 10);
          setTooltipStyle({
            left: x + 10,
            top: y + 10,
            opacity: 1,
            transform: "translate(0, 0)"
          });
        }
      });
    }
  }, [isTooltipLocked]);

  const handleRegionHoverLeave = useCallback(() => {
    if (!isTooltipLocked) {
      setHoveredRegion(null);
      setTooltipStyle(prev => ({ ...prev, opacity: 0 }));
    }
  }, [isTooltipLocked]);

  // Lägg till global klick-listener för att hantera klick utanför kartan
  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  useEffect(() => {
    if (svgRef.current) {
      const svgElements = svgRef.current.querySelectorAll("path");

      landsdelar.forEach((region) => {
        // Hitta alla paths som matchar region.id som prefix
        const matchingPaths = findPathsByPrefix(region.id);
        
        if (matchingPaths.length > 0) {
          // Applicera färg och events på alla matchande paths
          matchingPaths.forEach((path) => {
            path.setAttribute("fill", getRegionColor(region));

            // Klick-event för att markera region
            path.onclick = (e) => handleRegionClick(region, e);
            
            // Hover-events (endast när tooltip inte är låst)
            path.onmouseenter = (e) => handleRegionHover(region, e);
            path.onmousemove = handleRegionHoverMove;
            path.onmouseleave = handleRegionHoverLeave;
          });
        }
      });

      // Lägg till klick-event på hela kartan för att låsa upp tooltipen
      svgRef.current.onclick = handleMapClick;
    }
  }, [currentDate, getRegionColor, findPathsByPrefix, handleRegionClick, handleMapClick, handleRegionHover, handleRegionHoverMove, handleRegionHoverLeave]);

  // Uppdatera konturer för markerade regioner
  useEffect(() => {
    if (svgRef.current) {
      const allPaths = svgRef.current.querySelectorAll("path");
      
      allPaths.forEach(path => {
        // Ta bort alla tidigare konturer
        path.style.stroke = '';
        path.style.strokeWidth = '';
        path.style.strokeOpacity = '';
      });

      // Lägg till svart kontur på markerade regioner
      if (selectedRegion) {
        const matchingPaths = findPathsByPrefix(selectedRegion.id);
        matchingPaths.forEach(path => {
          path.style.stroke = '#000000'; // Svart kontur
          path.style.strokeWidth = '2px'; // 2px tjock
          path.style.strokeOpacity = '1'; // Full opacitet
        });
      }
    }
  }, [selectedRegion, findPathsByPrefix]);

  // Visa tooltip för vald region eller hoverad region
  const activeRegion = selectedRegion || hoveredRegion;

  return (
    <div className="relative w-full h-full bg-blue-50 overflow-hidden">
      <MapSVG ref={svgRef} className="w-full h-full" />

      {/* Tooltip */}
      {activeRegion && (
        <div
          ref={tooltipRef}
          className="absolute z-30 pointer-events-none"
          style={{
            position: "fixed",
            ...tooltipStyle,
          }}
        >
          {/* Förbättrad tooltip med vit bakgrund, svart text och snygg inramning */}
          <div 
            className={`bg-white border-2 rounded-lg shadow-xl p-4 text-sm text-black max-w-xs ${
              isTooltipLocked ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
            }`}
            style={{
              backgroundColor: 'white',
              border: isTooltipLocked ? '2px solid #3b82f6' : '2px solid #d1d5db',
              borderRadius: '8px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              padding: '16px',
              fontSize: '14px',
              color: 'black',
              maxWidth: '320px'
            }}
          >
            {/* Indikator för låst tooltip */}
            {isTooltipLocked && (
              <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                Låst
              </div>
            )}
            
            <div 
              className="font-semibold text-gray-900 mb-2"
              style={{
                fontWeight: '600',
                color: '#111827',
                marginBottom: '8px'
              }}
            >
              {activeRegion.landsdel}
            </div>
            <div 
              className="space-y-2"
              style={{ marginTop: '8px' }}
            >
              <div>
                <span 
                  className="font-semibold text-gray-800"
                  style={{ fontWeight: '600', color: '#374151' }}
                >
                  Start:
                </span> {activeRegion.startdatum}
              </div>
              <div 
                className="text-gray-700 italic"
                style={{ color: '#374151', fontStyle: 'italic' }}
              >
                {activeRegion.fritextStart}
              </div>

              {activeRegion.slutdatum && (
                <>
                  <div>
                    <span 
                      className="font-semibold text-gray-800"
                      style={{ fontWeight: '600', color: '#374151' }}
                    >
                      Slut:
                    </span> {activeRegion.slutdatum}
                  </div>
                  <div 
                    className="italic text-gray-700"
                    style={{ fontStyle: 'italic', color: '#374151' }}
                  >
                    {activeRegion.fritextSlut}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}